import { Worker, WorkerOptions } from "node:worker_threads";

import type { Disposable } from "../async/pool";
import { FixedPool } from "../async/pool";
import { noop } from "../TypingUtils";

interface Options {
    workerPath: string;
    workerOptions?: WorkerOptions;
    size: number;
}

interface Thread extends Disposable {
    worker: Worker;
}

export class ThreadPool {
    private pool?: FixedPool<Thread>;

    public constructor(ops: Options) {
        this.pool = new FixedPool({ size: ops.size, fabric: this.fabric.bind(this, ops) });
    }

    /**
     * @throws при вызове после {@link ThreadPool.dispose}
     */
    public exec<In, Out>(message: In): Promise<Out> {
        if (!this.pool) {
            return Promise.reject(new Error("ThreadPool: exec on disposed pool"));
        }
        return this.pool.exec(({ worker }) => this.call<In, Out>(worker, message));
    }

    public async dispose(): Promise<void> {
        await this.pool?.dispose();
        // даем GC собрать
        this.pool = undefined;
    }

    private fabric(ops: Options): Thread {
        const worker = new Worker(ops.workerPath, ops.workerOptions);
        return { worker: worker, dispose: () => worker.terminate().then(noop) };
    }

    private call<In, Out>(worker: Worker, message: In): Promise<Out> {
        const { promise, resolve, reject } = Promise.withResolvers<Out>();
        const cleanup = (): void => {
            worker.off("message", onMessage);
            worker.off("error", onError);
            worker.off("exit", onExit);
        };
        const onMessage = (out: Out): void => {
            cleanup();
            resolve(out);
        };
        const onError = (err: Error): void => {
            cleanup();
            reject(err);
        };
        const onExit = (code: number): void => {
            cleanup();
            reject(new Error(`Worker exited with code ${code}`));
        };
        worker.once("message", onMessage);
        worker.once("error", onError);
        worker.once("exit", onExit);
        worker.postMessage(message);
        return promise;
    }
}
